import { baseApi } from '@/lib/store/api/baseQuery'
import { apiEndpoints } from '@/lib/api/endpoints'

/** Each check-in personal information answer's name in the form, mapped to its name in the API. */
const FIELD_KEYS = {
    preferredFirstName: 'preferred_first_name',
    preferredLanguage: 'preferred_language',
    veteranStatus: 'veteran_status',
    maritalStatus: 'marital_status',
    employmentStatus: 'employment_status',
    race: 'race',
    ethnicity: 'ethnicity',
} as const

/** The check-in personal information form's answers, as option labels (blank when not on file). */
export type PersonalInformationValues = Record<keyof typeof FIELD_KEYS, string>

type PersonalInformationPayload = Record<(typeof FIELD_KEYS)[keyof typeof FIELD_KEYS], string>

interface PersonalInformationResponse {
    personal_information?: Partial<PersonalInformationPayload> | null
}

/** Maps an API payload to form values; an answer the payload omits is taken from `fallback`, or is blank. */
const fromPayload = (payload: Partial<PersonalInformationPayload>, fallback?: PersonalInformationValues) =>
    Object.fromEntries(
        Object.entries(FIELD_KEYS).map(([name, key]) => [
            name,
            payload[key] ?? fallback?.[name as keyof PersonalInformationValues] ?? '',
        ]),
    ) as PersonalInformationValues

const toPayload = (values: PersonalInformationValues) =>
    Object.fromEntries(
        Object.entries(FIELD_KEYS).map(([name, key]) => [key, values[name as keyof PersonalInformationValues]]),
    ) as PersonalInformationPayload

export const personalInformationApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getPersonalInformation: builder.query<PersonalInformationValues, void>({
            query: () => apiEndpoints.personalInformation.base,
            transformResponse: (response?: PersonalInformationResponse) =>
                fromPayload(response?.personal_information ?? {}),
            providesTags: ['PersonalInformation'],
        }),
        updatePersonalInformation: builder.mutation<PersonalInformationValues, PersonalInformationValues>({
            query: (values) => ({
                url: apiEndpoints.personalInformation.base,
                method: 'PATCH',
                body: toPayload(values),
            }),
            // An answer the response omits falls back to the submitted one, so the cache is never replaced with
            // blanks after a successful save.
            transformResponse: (response: PersonalInformationResponse | undefined, _meta, submittedValues) =>
                fromPayload(response?.personal_information ?? {}, submittedValues),
            // The owner applies an update asynchronously, so a refetch right after saving could return the old
            // answers; the cached read is replaced with what was just saved instead (so no tags are invalidated).
            async onQueryStarted(_values, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(personalInformationApi.util.updateQueryData('getPersonalInformation', undefined, () => data))
                } catch {
                    // The failed save is surfaced through the mutation result.
                }
            },
        }),
    }),
    overrideExisting: false,
})

export const { useGetPersonalInformationQuery, useUpdatePersonalInformationMutation } = personalInformationApi
